/**
 * Certificate types (frontend mirror of gg-be/utils/certificateServiceMap.js).
 *
 * Used by the contractor "upload certificate & mark complete" panel to:
 *   - populate the cert-type dropdown (the 10 valid document certificate types),
 *   - pre-select the type from the ticket's service NAME (never by ID identity — the
 *     ticket service and the document cert type are different `services` rows),
 *   - auto-fill the expiry date from the issue date per type.
 */

// documents.type_id → display name.
export const CERTIFICATE_TYPES = {
  6: "Gas Safe Certificate",
  7: "NIC Certificate",
  8: "Asbestos Register",
  9: "Fire Risk Assessment",
  10: "Legionella",
  29: "240V Certificate",
  30: "Fire Alarm",
  31: "Emergency Lighting",
  32: "Fire Extinguisher Certificate",
  33: "PAT Testing",
};

// documents.type_id → ticket service NAME (must match the `services` table).
const CERTIFICATE_TO_SERVICE_NAME = {
  6: "Gas Safety Certification",
  7: "EICR",
  8: "Asbestos Management",
  9: "Fire Risk Assessment",
  10: "Legionella Testing",
  29: "MTVH 240v Certificate",
  30: "Fire Alarm Service",
  31: "Emergency Lighting Service",
  32: "Fire Extinguisher Service",
  33: "PAT Testing",
};

// Inverse: normalised service name → cert type_id.
const SERVICE_NAME_TO_TYPE = Object.entries(CERTIFICATE_TO_SERVICE_NAME).reduce(
  (acc, [typeId, name]) => {
    acc[name.trim().toLowerCase()] = Number(typeId);
    return acc;
  },
  {}
);

// Default validity window (months) used to auto-fill expiry from the issue date.
const VALIDITY_MONTHS = {
  6: 12, // Gas Safety — annual
  7: 60, // EICR / NIC — 5 years
  8: 12, // Asbestos
  9: 12, // Fire Risk Assessment
  10: 24, // Legionella
  29: 60, // 240V
  30: 12, // Fire Alarm
  31: 12, // Emergency Lighting
  32: 12, // Fire Extinguisher
  33: 12, // PAT
};

// --- Certificate file constraints (PDF only, max 10 MB) ---
export const CERTIFICATE_FILE_TYPES = ["application/pdf"];
export const CERTIFICATE_FILE_EXTENSIONS = [".pdf"];
export const CERTIFICATE_MAX_SIZE = 10 * 1024 * 1024; // 10 MB
export const CERTIFICATE_ACCEPT = ".pdf,application/pdf";

/**
 * Validate a certificate file — accepts PDF up to 10 MB only.
 * Checks MIME type with an extension fallback (some browsers report an empty type).
 * @param {File} file
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateCertificateFile(file) {
  if (!file) return { isValid: false, error: "No file selected" };
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  const typeOk =
    CERTIFICATE_FILE_TYPES.includes(file.type) ||
    CERTIFICATE_FILE_EXTENSIONS.includes(ext);
  if (!typeOk) {
    return { isValid: false, error: "Certificates must be a PDF file." };
  }
  if (file.size > CERTIFICATE_MAX_SIZE) {
    return { isValid: false, error: "File is too large — the maximum certificate size is 10 MB." };
  }
  return { isValid: true, error: null };
}

/**
 * Derive the document cert type_id from a ticket's service name.
 * Returns null when the service isn't a recognised certificate service
 * (caller should then let the user pick the type manually).
 */
export function certificateTypeFromServiceName(serviceName) {
  if (!serviceName || typeof serviceName !== "string") return null;
  return SERVICE_NAME_TO_TYPE[serviceName.trim().toLowerCase()] ?? null;
}

/** Default validity window in months for a cert type (defaults to 12). */
export function validityMonthsForType(typeId) {
  return VALIDITY_MONTHS[Number(typeId)] || 12;
}
